import { Grid } from '@material-ui/core';
import moment from 'moment';
import { useCallback, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { get } from 'lodash';
import { fetchById } from '../../features/modelo-informacoes-complementares/modeloInformacoesComplementaresAPI';
import { setErrorFeedback, setWarningFeedback } from '../../features/feedback/feedbackSlice';
import { ERROR_SEVERITY, WARNING_SEVERITY } from '../../features/severity';
import QCXButton from '../../shared-components/button/QCXButton';
import QCXFinalMultilineTextField from '../../shared-components/final-text-field/QCXFinalMultilineTextField';
import QCXFormStepWizard from '../../shared-components/form-step-wizard/QCXFormStepWizard';
import QCXAutocompleteModeloInformacaoComplementar from '../../shared-components/modelo-informacao-complementar/QCXAutocompleteModeloInformacaoComplementar';
import { isDanfeFilhote } from '../../utils/general/danfe/danfeUtils';
import QCXFormTitle from '../form-title/QCXFormTitle';
import QCXConfirmDialog from '../../shared-components/dialog/QCXConfirmDialog';

const GridContainer = ({ children }) => (
  <Grid container spacing={2} style={{ marginTop: '1rem' }}>
    {children}
  </Grid>
);

export default function QCXDanfeInformacoesComplementaresForm({
  isCreate,
  isConsult,
  isUpdate,
  initialValues,
  handleSubmit,
  handleChangeToUpdate,
  handleCancelUpdate,
  controlComponentProps,
  ...restProps
}) {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const resolveSubmitRef = useRef(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitData, setSubmitData] = useState(null);

  const isFilhote = useMemo(() => isDanfeFilhote(initialValues?.tipoDanfe), [initialValues]);
  const currentDate = useMemo(() => moment().toDate(), []);
  const model = useMemo(
    () =>
      isCreate
        ? {
            ...initialValues,
            dataEntrada: isFilhote ? initialValues?.dataEntrada : currentDate,
            dataEmissao: isFilhote ? initialValues?.dataEmissao : currentDate,
          }
        : initialValues,
    [initialValues, currentDate, isCreate, isFilhote]
  );

  const handleImpediment = useCallback((impediment) => {
    if (impediment?.severity === WARNING_SEVERITY) {
      dispatch(
        setWarningFeedback({
          message: impediment?.message,
        })
      );
      return;
    }
    if (impediment?.severity === ERROR_SEVERITY) {
      dispatch(
        setErrorFeedback({
          message: impediment?.message,
        })
      );
    }
  }, []);

  const handleAdd = useCallback(
    async (form, values, event) => {
      console.log('form', form, values, event);
      event?.stopPropagation();

      try {
        const selectedModeloInformacaoComplementarId = get(values, 'modeloInformacaoComplementar.id');

        if (!selectedModeloInformacaoComplementarId) {
          const emptySelectionErrorMessage = t(
            'com.muralis.qcx.mensagem.nenhumModeloInformacoesComplementaresSelecionado'
          );

          handleImpediment({
            severity: WARNING_SEVERITY,
            message: emptySelectionErrorMessage,
          });

          return;
        }

        const currentInformacaoComplementar = get(values, 'infoAdicionalCompleta');

        const modeloInformacaocomplementarResponse = await fetchById(selectedModeloInformacaoComplementarId);

        if (modeloInformacaocomplementarResponse.status === 200) {
          const foundModeloInformacaoComplementar = modeloInformacaocomplementarResponse?.data;
          const foundModeloComplemento = foundModeloInformacaoComplementar?.complemento || '';

          const formattedModeloInformacaoComplementar = foundModeloComplemento;

          const newLengthPredictedOfInformacoesComplementares =
            currentInformacaoComplementar?.length + formattedModeloInformacaoComplementar.length;

          const informacoesComplementaresMaxLength = 5000;

          if (newLengthPredictedOfInformacoesComplementares > informacoesComplementaresMaxLength) {
            const errorMessage = t('com.muralis.qcx.mensagem.naoPossivelAdicionarModeloInformacoesComplementares');
            const errorReason = t('com.muralis.qcx.validacao.limiteMaximoCaracteresInformacoesComplementaresExcedido');
            const errorDetails = `(${newLengthPredictedOfInformacoesComplementares} / ${informacoesComplementaresMaxLength})`;
            const exceddedLengthErrorMessage = `${errorMessage}: ${errorReason} ${errorDetails}.`;

            throw new Error(exceddedLengthErrorMessage);
          }

          const concatenatedInformacaoComplementar = currentInformacaoComplementar
            ? `${currentInformacaoComplementar}\n\n${formattedModeloInformacaoComplementar}`
            : formattedModeloInformacaoComplementar;

          form.change('infoAdicionalCompleta', concatenatedInformacaoComplementar);
        }
      } catch (error) {
        const errorMessage = error?.response?.message
          ? t('com.muralis.qcx.erro.erroCarregarModeloInformacoesComplementares', {
              erro: error?.response?.message,
            })
          : error?.message;

        handleImpediment({
          severity: ERROR_SEVERITY,
          message: errorMessage,
        });
      }
    },
    [handleImpediment]
  );

  const handleSubmitOpenModal = (data, step, next) => {
    setShowConfirmModal(true);
    setSubmitData({ data, step, next });

    return new Promise((resolve) => {
      resolveSubmitRef.current = resolve;
    });
  };

  const handleSubmitConfirm = async (gerarComplementares) => {
    if (!submitData) return;

    const { data, step, next } = submitData;

    if (gerarComplementares) {
      data.gerarComplementares = true;
    }

    try {
      await handleSubmit(data, step, next);
    } catch (error) {
      console.error('Submission failed:', error);
    } finally {
      setShowConfirmModal(false);
      setSubmitData(null);
      if (resolveSubmitRef.current) {
        resolveSubmitRef.current();
        resolveSubmitRef.current = null;
      }
    }
  };

  return (
    <QCXFormStepWizard
      isConsult={isConsult}
      isUpdate={isUpdate}
      disablePristine
      initialValues={model}
      handleSubmitValues={handleSubmitOpenModal}
      handleChangeToUpdate={handleChangeToUpdate}
      handleCancelUpdate={handleCancelUpdate}
      onSubmit={handleSubmitOpenModal}
      controlComponentProps={controlComponentProps}
      {...restProps}
    >
      {({ form, values }) => (
        <>
          <GridContainer>
            <Grid
              item
              xs={12}
              style={{
                paddingTop: '0px',
              }}
            >
              <QCXFormTitle title="Informações complementares" />
            </Grid>
            <Grid item xs={12} sm={9} md={9}>
              <QCXAutocompleteModeloInformacaoComplementar
                id="autocomplete-field-modelo-informacao-complementar"
                key="autocomplete-field-modelo-informacao-complementar"
                name="modeloInformacaoComplementar.id"
                label={t('com.muralis.qcx.modeloInformacaoComplementar.label')}
                disabled={isConsult}
                initialValues={values}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={3}>
              <QCXButton
                id="control-box-modelo-informacao-complementar-add-button"
                key="control-box-modelo-informacao-complementar-add-button"
                color="secondary"
                onClick={(event) => handleAdd(form, values, event)}
                fullWidth
                disabled={isConsult}
              >
                {t('com.muralis.qcx.acoes.adicionar')}
              </QCXButton>
            </Grid>
            <Grid item xs={12}>
              <QCXFinalMultilineTextField
                id="text-informacoes-complementares-completa-multiline-field"
                key="text-informacoes-complementares-completa-multiline-field"
                name="infoAdicionalCompleta"
                label={t('com.muralis.qcx.informacoesComplementaresInteresseFisco')}
                disabled={isConsult}
                rows={9}
                maxLength={5000}
                remaningCharactersInfo
                helperText={t('com.muralis.qcx.mensagem.dadosGeradosReferenciaModeloInformacoesComplementares')}
              />
            </Grid>
          </GridContainer>

          <QCXConfirmDialog
            open={showConfirmModal}
            title="Deseja recalcular a informação complementar?"
            content="Isso irá sobrescrever alterações feitas no texto complementar."
            onConfirm={() => handleSubmitConfirm(true)}
            onClose={() => handleSubmitConfirm(false)}
            buttonGroupOptions={{
              confirm: {
                description: 'Sim',
              },
              cancel: {
                description: 'Não',
              },
            }}
          />
        </>
      )}
    </QCXFormStepWizard>
  );
}
